import { Button, Typography } from '@mui/material'
import { grey } from '@mui/material/colors'
import { Icon } from 'components/icon'
import { theme } from 'config'
import { File } from 'models'
import React, { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const getColor = (props: any) => {
  if (props.isDragAccept) {
    return theme.palette.success.main
  }
  if (props.isDragReject) {
    return theme.palette.error.main
  }
  if (props.isFocused) {
    return theme.palette.primary.main
  }
  return grey[300]
}

const UploadForm = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 0;
  padding: ${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(5)};
  border-width: 2px;
  border-color: ${(props) => getColor(props)};
  border-style: dashed;
  background-color: transparent;
  outline: none;
  transition: border 0.3s ease-in-out;
`

const LargeIcon = styled(Icon)`
  width: 8rem;
  height: 8rem;

  svg {
    width: 100%;
    height: 100%;
  }
`

type UploadProps = {
  onUploadComplete?: (data: string | ArrayBuffer) => void
  setMime?: (mime: string) => void
  setSize?: (size: number) => void
  setName?: (name: string) => void
}

const UploadFile = (props: UploadProps) => {
  const { t, i18n } = useTranslation()

  const onDrop = useCallback((acceptedFiles: any[]) => {
    acceptedFiles.forEach((fileData: any) => {
      const reader = new FileReader();

      reader.onabort   = () => console.error('file reading was aborted');
      reader.onerror   = () => console.error('file reading has failed');
      reader.onloadend = () => props.onUploadComplete(reader.result);

      props.setName(fileData.name);
      props.setSize(fileData.size);
      props.setMime(fileData.type);
      reader.readAsDataURL(fileData);
    })
  }, [])

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({ onDrop })

  return (
    <UploadForm {...getRootProps({ isFocused, isDragAccept, isDragReject })}>
      <input {...getInputProps()} />
      <LargeIcon iconId='download' />
      <Typography>{t('drag_and_drop_description')}</Typography>
      <Typography>{t('common_or')}</Typography>
      <Button variant='outlined'>{t('common_browse')}</Button>
    </UploadForm>
  )
}

type FileUploadProps = {
  onUpload?:    (file: File) => void,
  isThumbnail?: boolean,
}

export const FileUpload = (props: FileUploadProps) => {
  const [fileData, setFileData] = useState(null);
  const [mimeType, setMimeType] = useState(null);
  const [size, setSize] = useState(null);
  const [name, setName] = useState(null);

  useEffect(() => {
    if (!fileData || !mimeType || !size || !name) return;
    const f = File.create({
      title:        name,
      uri:          fileData,
      mime_type:    mimeType,
      size:         size,
      is_thumbnail: props.isThumbnail,
    });
    props.onUpload(f)
  }, [fileData, mimeType, size]);

  return (
    <UploadFile
      onUploadComplete = {setFileData}
      setMime          = {setMimeType}
      setSize          = {setSize}
      setName          = {setName}
    />
  )
}
